/**
 * Background Runs — WHAT happened. Engine-written and never editable, so this
 * tab is read-only by design.
 *
 * The important display rule: a job with maxRetries: 2 that fails end-to-end
 * writes THREE documents (RETRYING, RETRYING, FAILED) sharing one
 * runCorrelationId. Rendered flat, one incident reads as three. Rows are
 * therefore grouped per fire.
 *
 * A row opens that run's own page (run-view.tsx) — every attempt, its
 * parameters and output, Stop and Run again — the same list → view shape as
 * the other masters, rather than a modal that could only be read and closed.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'sharedUi/Calendar';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import { formatTimestamp } from 'sharedUi/dateUtils';
import SaasDataTable, { type SaasColumn } from '../../../components/common/saas-data-table';
import { useAppNavigate } from '../../../hooks/useAppNavigate';
import { backgroundJobService } from '../../../services/background-job.service';
import type { RunGroup } from '../../../types/background-job.types';
import { RUN_STATUS_OPTIONS, TRIGGERED_BY_OPTIONS, toNaiveIso } from './shared';
import { RUN_STATUS_SEVERITY, groupRuns, latestAttempt, runDuration } from '../run-format';
import { runPath } from '../view-paths';

const RunsTab = () => {
  const navigate = useAppNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [jobFilter, setJobFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  const jobsQ = useQuery({
    queryKey: ['bg-jobs', 'all-for-runs'],
    queryFn: () => backgroundJobService.listJobs({ page: 1, page_size: 200 }),
    staleTime: 60_000,
  });
  const jobs = useMemo(() => jobsQ.data?.items ?? [], [jobsQ.data]);

  /* Attempts are fetched, then folded into fires. A page of attempts yields
     fewer rows than it contains, so the page size is deliberately generous. */
  const runsQ = useQuery({
    queryKey: ['bg-runs', { page, pageSize, jobFilter, statusFilter, triggerFilter, from, to }],
    queryFn: () => backgroundJobService.listRuns({
      page,
      page_size: pageSize,
      jobId: jobFilter || undefined,
      status: statusFilter || undefined,
      triggeredBy: triggerFilter || undefined,
      from: toNaiveIso(from),
      to: toNaiveIso(to),
      sort_by: 'startedAt',
      sort_order: 'desc',
    }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const groups: RunGroup[] = useMemo(() => {
    const byId = new Map(jobs.map((j) => [j._id, j]));
    return groupRuns(runsQ.data?.items ?? [], (jobId) => byId.get(jobId)?.jobName || jobId);
  }, [runsQ.data, jobs]);

  const columns: SaasColumn<RunGroup>[] = useMemo(() => [
    {
      field: 'startedAt', header: 'Started', hideable: false, style: { minWidth: 165 },
      body: (g) => <span className="text-xs">{g.startedAt ? formatTimestamp(g.startedAt) : '—'}</span>,
    },
    {
      field: 'jobName', header: 'Job', style: { minWidth: 200 },
      body: (g) => <span className="text-sm font-medium text-ink">{g.jobName}</span>,
    },
    {
      field: 'status', header: 'Status', align: 'center', style: { width: 130 },
      body: (g) => <Tag value={g.status} severity={RUN_STATUS_SEVERITY[g.status]} />,
    },
    {
      field: 'attempts', header: 'Attempts', align: 'center', style: { width: 110 },
      body: (g) => (g.attempts.length > 1
        ? <Tag value={`${g.attempts.length} attempts`} severity="warning" />
        : <span className="text-xs text-gray-400">1</span>),
    },
    {
      field: 'triggeredBy', header: 'Triggered By', align: 'center', style: { width: 140 },
      body: (g) => <Tag value={g.triggeredBy === 'MANUAL_USER' ? 'Manual' : 'Scheduler'}
        severity={g.triggeredBy === 'MANUAL_USER' ? 'info' : 'secondary'} />,
    },
    {
      field: 'duration', header: 'Duration', align: 'right', style: { width: 110 },
      body: (g) => <span className="text-xs">{runDuration(g)}</span>,
    },
    {
      field: 'actions', header: '', hideable: false, align: 'center', style: { width: 70 },
      // Opens on the latest attempt, which carries the fire's final status.
      body: (g) => <Button aria-label="View" icon="pi pi-eye" text rounded severity="success"
        tooltip="Attempts and output" onClick={() => navigate(runPath(latestAttempt(g)._id))} />,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <SaasDataTable
      tableId="background-runs"
      columns={columns}
      data={groups}
      loading={runsQ.isFetching}
      dataKey="runCorrelationId"
      emptyMessage="No runs recorded. If schedules exist but never fire, check that the job engine is enabled."
      first={(page - 1) * pageSize}
      rows={pageSize}
      totalRecords={groups.length < pageSize && page === 1 ? groups.length : (runsQ.data?.total ?? 0)}
      onPage={(e: { first: number; rows: number }) => { setPageSize(e.rows); setPage(Math.floor(e.first / e.rows) + 1); }}
      rowsPerPageOptions={[25, 50, 100]}
      onSort={() => { /* fires are ordered by start time */ }}
      filters={{}}
      // No column declares `filter`, so this is never called. The toolbar owns
      // filtering because every filter here is SERVER-side (jobId, status,
      // trigger, date range) — a column filter would have searched the current
      // page of attempts, which is not the same set as the fires on screen.
      onFilter={() => { /* no column filters: the toolbar filters server-side */ }}
      testId="BackgroundRuns"
      toolbarStart={
        <div className="flex flex-wrap items-center gap-2">
          {/* Widths are deliberately tight. This toolbar also renders inside the
              dashboard's Records panel, which is narrower than the full-page tab,
              and at the old sizes the date range wrapped onto a second line — a
              "To" under its "From" reads as a separate filter, not a range. */}
          <Dropdown value={jobFilter}
            options={[{ label: 'All Jobs', value: '' }, ...jobs.map((j) => ({ label: j.jobName, value: j._id }))]}
            optionLabel="label" optionValue="value" placeholder="All Jobs" filter resetFilterOnHide
            showClear={!!jobFilter} style={{ minWidth: 150, maxWidth: 190 }}
            onChange={(e: { value: string }) => { setJobFilter(e.value || ''); setPage(1); }} />
          <Dropdown value={statusFilter}
            options={[{ label: 'All Statuses', value: '' }, ...RUN_STATUS_OPTIONS]}
            optionLabel="label" optionValue="value" placeholder="All Statuses"
            showClear={!!statusFilter} style={{ minWidth: 132 }}
            onChange={(e: { value: string }) => { setStatusFilter(e.value || ''); setPage(1); }} />
          <Dropdown value={triggerFilter}
            options={[{ label: 'Any Trigger', value: '' }, ...TRIGGERED_BY_OPTIONS]}
            optionLabel="label" optionValue="value" placeholder="Any Trigger"
            showClear={!!triggerFilter} style={{ minWidth: 132 }}
            onChange={(e: { value: string }) => { setTriggerFilter(e.value || ''); setPage(1); }} />
          {/* The two halves of ONE range, so they are wrapped in a flex-nowrap box:
              as loose siblings of the dropdowns they wrapped independently, and a
              "To" sitting on the line below its "From" reads as a separate filter
              rather than the end of a range. */}
          <div className="flex flex-nowrap items-center gap-1">
            <Calendar value={from} onChange={(e) => { setFrom((e.value as Date) ?? null); setPage(1); }}
              showIcon showButtonBar dateFormat="dd M yy" placeholder="From"
              maxDate={to ?? new Date()} style={{ width: 124 }}
              appendTo={typeof document !== 'undefined' ? document.body : undefined} />
            <span className="shrink-0 px-0.5 text-xs text-gray-400">to</span>
            <Calendar value={to} onChange={(e) => { setTo((e.value as Date) ?? null); setPage(1); }}
              showIcon showButtonBar dateFormat="dd M yy" placeholder="To"
              minDate={from ?? undefined} maxDate={new Date()} style={{ width: 124 }}
              appendTo={typeof document !== 'undefined' ? document.body : undefined} />
          </div>
        </div>
      }
      toolbarEnd={
        <span className="text-xs text-gray-400">
          {groups.length} fire{groups.length === 1 ? '' : 's'} from {runsQ.data?.items?.length ?? 0} attempts
        </span>
      }
    />
  );
};

export default RunsTab;
